import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import './App.css';
import './shared/styles/tokens.css';

import { useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { modeFromPathname } from 'app/mode-from-path';
import { SubmissionOverlay } from 'app/SubmissionOverlay';
import Modal from 'shared/components/Modal.tsx';
import { SlackLoginScreen } from 'features/auth/SlackLoginScreen.tsx';
import { useSlackAuth } from 'features/auth/SlackAuthContext.tsx';
import { Badge, getBadgeBackdropImageUrls, useBadges } from 'features/catalog/useBadges.ts';
import { useFeedUpdates, withFeedActivityOnProfiles } from 'features/feed/useFeedUpdates.ts';
import { TalosDataProvider } from 'shared/context/TalosDataContext.tsx';
import { LOADING_LABEL_MIN_VISIBLE_MS, useLoadingLabel } from 'shared/hooks/useGracePeriodVisible.ts';
import { useProfiles } from 'features/profiles/useProfiles.ts';
import { AppShell } from 'shared/layout/AppShell.tsx';
import { ShellNavId } from 'shared/layout/Sidebar.tsx';
import { BadgeModes } from 'types/badge-modes';
import {
  buildContributorIndex,
  contributorBadgeCount,
  getContributorIds,
} from 'features/catalog/badge-contributors.ts';
import { BADGE_SUBMISSION_URL } from 'config/data-sources';
import { talosBadgesFaviconURL, talosBadgesLogoURL } from 'config/constants.ts';
import { ensureAppFonts } from 'shared/lib/display-fonts.ts';
import { getFirstPartOfEmail } from 'shared/lib/email.ts';
import { prefetchImage } from 'shared/lib/image-prefetch.ts';
import { buildEligibleOwnershipBadgesMap } from 'features/claim/badge-claim-eligibility.ts';
import {
  getDevEmptyProfile,
  getDevMockProfileID,
  setDevEmptyProfilePersisted,
  setDevMockProfileIDPersisted,
} from 'shared/lib/dev-mock-profile.ts';
import { isTutorialComplete, parseBattleRoomId } from 'features/battle/index';
import {
  BATTLE_BADGES_CLAIMED_PATH,
  BATTLE_BADGES_PATH,
  BATTLE_HOW_TO_PATH,
  CLAIM_PATH,
  battleModeForPath,
  claimPath,
  contributionsPath,
  isClaimFlowPath,
  isClaimSelectSegment,
  isContributionsSegment,
  parseContributionsTab,
  pathForNav,
  RESERVED_PATHS,
  SUMMARY_PATH,
} from 'shared/lib/nav-paths.ts';
import { applyShareMetaToDocument, shareMetaForPath } from 'shared/lib/apply-share-meta.ts';
import { setPendingClaimBadgeId } from 'features/claim/claim-prime.ts';
import { getRarityImageUrlByName, RARITY_ORDER } from 'features/catalog/rarity.ts';

export const App = () => {
  const { isAuthenticated, isAuthenticating, user, logout } = useSlackAuth();
  const [imagesReady, setImagesReady] = useState(false);
  // Boot: badge catalog + field thumbs/logo first (field visible under overlay), then profiles.
  const { badgesMap } = useBadges();
  const [updatedBadgesMap, setUpdatedBadgesMap] = useState<Map<string, Badge> | null>(null);
  const {
    profilesMap,
    rankedProfiles,
    submissionsByEmail,
    refreshProfiles,
    isRefreshing,
    profilesReady,
    profilesError,
  } = useProfiles(badgesMap, imagesReady && isAuthenticated && !isAuthenticating);

  useEffect(() => {
    if (!profilesReady) return;
    // Recompute ownership whenever profiles reload (initial load + feed Refresh).
    // Claim % uses feed-visible submissions after the catalog drop; Claimed By lists live owners.
    setUpdatedBadgesMap(buildEligibleOwnershipBadgesMap(badgesMap, profilesMap, submissionsByEmail));
  }, [badgesMap, profilesMap, profilesReady, submissionsByEmail]);

  // Boot image order: favicon (spinner) → shuffled field thumbs + rarities → full logo last.
  // Prefetch retains decoded Images in a keep-alive host so navigation remounts stay warm.
  // LoginBadgeBackdrop reveals those same cached thumbs progressively while this gate is pending.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // 1) Kick favicon first (don't block — spinner + label should appear immediately).
      void prefetchImage(talosBadgesFaviconURL);

      // 2) Field thumbs + rarity gems (rarity was never warmed before — hover/nav felt cold).
      const thumbs = [...getBadgeBackdropImageUrls()];
      for (let i = thumbs.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [thumbs[i], thumbs[j]] = [thumbs[j], thumbs[i]];
      }
      const rarityUrls = RARITY_ORDER.map((name) => getRarityImageUrlByName(name));
      await Promise.all([...thumbs, ...rarityUrls].map((url) => prefetchImage(url)));
      if (cancelled) return;

      // 3) Regular logo last — used after the field is already filling in.
      await prefetchImage(talosBadgesLogoURL);
      if (!cancelled) setImagesReady(true);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  const [selectedProfileID, setSelectedProfileID] = useState<string | null>(null);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  /** When set, ClaimBadgesView auto-claims this badge and opens Summarize & Claim. */
  const [claimBadgePrime, setClaimBadgePrime] = useState<{
    id: string;
  } | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [devMockProfileID, setDevMockProfileID] = useState<string | null>(() =>
    process.env.NODE_ENV === 'development' && !getDevEmptyProfile() ? getDevMockProfileID() : null
  );
  const [devEmptyProfile, setDevEmptyProfile] = useState(
    () => process.env.NODE_ENV === 'development' && getDevEmptyProfile()
  );
  /** Feed-local profile focus (search / ←→ / standing). Used by Dev Edit. */
  const [feedFocusId, setFeedFocusId] = useState<string | null>(null);
  const realMyProfileID = user?.email ? getFirstPartOfEmail(user.email) : null;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    setDevMockProfileIDPersisted(devMockProfileID);
    setDevEmptyProfilePersisted(devEmptyProfile);
  }, [devMockProfileID, devEmptyProfile]);

  // Drop a persisted mock if that profile is no longer in the sheet.
  useEffect(() => {
    if (!devMockProfileID || profilesMap.size === 0) return;
    if (!profilesMap.has(devMockProfileID)) setDevMockProfileID(null);
  }, [devMockProfileID, profilesMap]);

  // Empty / no-profile preview: no own profile id, and hide your real entry from lists/search.
  const myProfileID =
    process.env.NODE_ENV === 'development' && devEmptyProfile
      ? null
      : process.env.NODE_ENV === 'development' && devMockProfileID
        ? devMockProfileID
        : realMyProfileID;

  const effectiveProfilesMap = useMemo(() => {
    if (!devEmptyProfile || !realMyProfileID || !profilesMap.has(realMyProfileID)) return profilesMap;
    const next = new Map(profilesMap);
    next.delete(realMyProfileID);
    return next;
  }, [devEmptyProfile, profilesMap, realMyProfileID]);

  const effectiveRankedProfiles = useMemo(() => {
    if (!devEmptyProfile || !realMyProfileID) return rankedProfiles;
    return rankedProfiles.filter((p) => getFirstPartOfEmail(p.email) !== realMyProfileID);
  }, [devEmptyProfile, rankedProfiles, realMyProfileID]);

  const contributorIndex = useMemo(() => buildContributorIndex(badgesMap), [badgesMap]);

  const myProfile = myProfileID ? (effectiveProfilesMap.get(myProfileID) ?? null) : null;
  /** True when the signed-in (or mocked) identity already has a sheet profile. */
  const hasOwnProfile = !!myProfile;

  const [mode, setMode] = useState<BadgeModes>(() => modeFromPathname(location.pathname));
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [showSubmissionScreen, setShowSubmissionScreen] = useState(false);
  const [submissionCode, setSubmissionCode] = useState('');
  const [submissionCopiedFlash, setSubmissionCopiedFlash] = useState(false);

  useEffect(() => {
    if (location.pathname === '/leaderboard') return;
    const scrollable = document.querySelector('.app-shell-main');
    if (scrollable) scrollable.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname, mode]);

  const dataReady = useMemo(
    () => badgesMap.size > 0 && profilesReady && updatedBadgesMap != null,
    [badgesMap.size, profilesReady, updatedBadgesMap]
  );

  // Browser tab / in-app WebView titles. Slack/Discord use the Netlify edge function instead.
  // Feed stays “Talos Badges” until profiles finish loading, then “Talos Badges Feed”.
  useEffect(() => {
    const pageUrl = `${window.location.origin}${location.pathname}${location.search}`;
    if (showSubmissionScreen) {
      applyShareMetaToDocument(
        { title: 'Submission Ready', description: 'Your Talos Badges submission is ready.' },
        pageUrl
      );
      return;
    }
    const profileSeg = location.pathname.split('/').filter(Boolean)[0];
    const profileDisplayName =
      profileSeg && !RESERVED_PATHS.has(profileSeg) ? effectiveProfilesMap.get(profileSeg)?.name : undefined;
    applyShareMetaToDocument(
      shareMetaForPath(location.pathname, {
        profileDisplayName,
        isAuthenticated: isAuthenticated && dataReady,
      }),
      pageUrl
    );
  }, [location.pathname, location.search, effectiveProfilesMap, isAuthenticated, dataReady, showSubmissionScreen]);

  const [bootOverlay, setBootOverlay] = useState(true);
  const [bootLeaving, setBootLeaving] = useState(false);
  const [backdropFieldReady, setBackdropFieldReady] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  // Post-submit Refresh reuses the boot overlay and walks Badges → Profiles again.
  const [forcedLoadingPhase, setForcedLoadingPhase] = useState<string | null>(null);
  const [loadingSnapEpoch, setLoadingSnapEpoch] = useState(0);
  const badgesStepDone = imagesReady && backdropFieldReady && badgesMap.size > 0;
  const loadingPhase = forcedLoadingPhase ?? (badgesStepDone ? 'Loading Profiles' : 'Loading Badges');
  // Stay pending through the badges step so we actually enter "Loading Profiles"
  // (profiles can finish while the field is still animating — previously we dismissed early).
  const loadingPending =
    isAuthenticated &&
    !isAuthenticating &&
    !showSubmissionScreen &&
    (forcedLoadingPhase != null || !badgesStepDone || !dataReady);
  const { visible: showLoadingMessage, label: loadingLabel } = useLoadingLabel(
    loadingPhase,
    loadingPending,
    LOADING_LABEL_MIN_VISIBLE_MS,
    loadingSnapEpoch
  );

  useEffect(() => {
    let cancelled = false;
    ensureAppFonts().then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Crossfade loading → shell. Field scrolls underneath while thumbs/logo load, then profiles.
  // Overlay stays until images + profiles + field enter + fonts are done, and the loading
  // label has finished its minimum on-screen time. Once dismissed after a successful boot,
  // never re-open (zoom/resize can briefly unset field-ready while the backdrop rebuilds).
  // Stay armed while logged out — login uses its own screen, but we must still show
  // Loading Badges/Profiles after auth (do not clear bootOverlay here).
  useEffect(() => {
    if (!bootOverlay) return;
    if (!isAuthenticated) return;
    if (!imagesReady || !dataReady || !backdropFieldReady || !fontsReady || showLoadingMessage) {
      setBootLeaving(false);
      return;
    }

    const leaveTimer = window.setTimeout(() => setBootLeaving(true), 70);
    // Hold until shell chrome mounts (sidebar 140ms + content +100ms = 240ms), then finish the fade.
    const hideTimer = window.setTimeout(() => {
      setBootOverlay(false);
      setBootLeaving(false);
    }, 420);
    return () => {
      window.clearTimeout(leaveTimer);
      window.clearTimeout(hideTimer);
    };
  }, [imagesReady, dataReady, backdropFieldReady, fontsReady, showLoadingMessage, isAuthenticated, bootOverlay]);

  const handleSetProfileID = useCallback(
    (email: string | null) => {
      const profileID = email ? getFirstPartOfEmail(email) : null;
      setSelectedProfileID(profileID);
      if (profileID == null) navigate('/');
      else navigate(`/${profileID}`);
    },
    [navigate]
  );

  useEffect(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const [seg0, seg1] = parts;

    if (!seg0) {
      setSelectedProfileID(null);
      setFeedFocusId(null);
      setMode('splash');
      return;
    }

    if (seg0 === 'battle') {
      setSelectedProfileID(null);
      if (!seg1 || location.pathname === BATTLE_BADGES_CLAIMED_PATH) {
        navigate(BATTLE_BADGES_PATH, { replace: true });
        return;
      }
      const nextMode = battleModeForPath(seg1);
      setMode(nextMode);
      const roomId = parseBattleRoomId(location.pathname);
      const gated = (nextMode === 'badges_battle' && !roomId) || nextMode === 'battle_arena';
      if (gated && !isTutorialComplete()) {
        navigate(BATTLE_HOW_TO_PATH, { replace: true });
      }
      return;
    }

    if (!seg1) {
      switch (seg0) {
        case 'gallery':
          // Legacy `/gallery` → badges gallery
          navigate('/gallery/badges', { replace: true });
          return;
        case 'journeys':
          // Legacy `/journeys` gallery → `/gallery/journeys`
          navigate(
            {
              pathname: '/gallery/journeys',
              search: location.search,
              hash: location.hash,
            },
            { replace: true }
          );
          return;
        case 'stats':
          setSelectedProfileID(null);
          setMode('stats');
          return;
        case 'leaderboard':
          setSelectedProfileID(null);
          setMode('leaderboard');
          return;
        case 'explore':
          setSelectedProfileID(null);
          setFeedFocusId(null);
          setMode('explore');
          return;
        case 'create':
          // Create is only for people without a profile (and Dev “No Profile”).
          if (hasOwnProfile) {
            navigate(myProfileID ? `/${myProfileID}` : '/', { replace: true });
            return;
          }
          setSelectedProfileID(null);
          setMode('editing_profile');
          return;
        case 'edit':
          navigate(`${CLAIM_PATH}${location.search}${location.hash}`, { replace: true });
          return;
        case 'claim':
          if (hasOwnProfile && myProfileID) {
            setSelectedProfileID(myProfileID);
            setMode('editing_profile');
            return;
          }
          // Profiles still loading — stay on /claim. Never bounce a known user through /create.
          if (effectiveProfilesMap.size === 0) {
            setMode('editing_profile');
            return;
          }
          setSelectedProfileID(null);
          setMode('editing_profile');
          navigate('/create', { replace: true });
          return;
        case 'summary':
          // Summarize step for /claim (existing profile) or /create (no profile).
          if (hasOwnProfile && myProfileID) {
            setSelectedProfileID(myProfileID);
            setMode('editing_profile');
            return;
          }
          if (effectiveProfilesMap.size === 0) {
            setMode('editing_profile');
            return;
          }
          setSelectedProfileID(null);
          setMode('editing_profile');
          return;
        case 'feed':
          setSelectedProfileID(null);
          setFeedFocusId(null);
          setMode('splash');
          navigate('/', { replace: true });
          return;
        default:
          if (effectiveProfilesMap.has(seg0)) {
            setSelectedProfileID(seg0);
            setMode('collected');
            return;
          }
          if (effectiveProfilesMap.size > 0) {
            setSelectedProfileID(null);
            setMode('splash');
            navigate('/', { replace: true });
          }
          return;
      }
    }

    if (seg0 === 'stats') {
      setSelectedProfileID(null);
      if (seg1 === 'hashes') {
        setMode('badge_hashes');
        return;
      }
      setMode('stats');
      if (seg1) navigate('/stats', { replace: true });
      return;
    }

    if (seg0 === 'gallery') {
      setSelectedProfileID(null);
      if (seg1 === 'badges') {
        setMode('badge_gallery');
        return;
      }
      if (seg1 === 'journeys') {
        setMode('journey_gallery');
        return;
      }
      navigate('/gallery/badges', { replace: true });
      return;
    }

    if (effectiveProfilesMap.has(seg0)) {
      setSelectedProfileID(seg0);
      if (seg1 === 'journeys') {
        setMode('journeys');
        return;
      }
      if (isContributionsSegment(seg1)) {
        setMode('submitted');
        if (seg1 === 'submitted') {
          navigate(contributionsPath(seg0, parseContributionsTab(location.hash)), { replace: true });
        }
        return;
      }
      if (seg1 === 'feed') {
        setMode('profile_feed');
        return;
      }
      if (isClaimSelectSegment(seg1)) {
        navigate(`${CLAIM_PATH}${location.search}${location.hash}`, { replace: true });
        return;
      }
      setMode('collected');
      navigate(`/${seg0}`, { replace: true });
      return;
    }

    if (effectiveProfilesMap.size > 0) {
      setSelectedProfileID(null);
      setMode('splash');
      navigate('/', { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate, effectiveProfilesMap, myProfileID, hasOwnProfile]);

  const handleSubmissionTriggered = useCallback(
    (pasteCode: string) => {
      // Commit overlay + clear "unsaved edit" before navigating so the claim
      // leave-blocker cannot keep the URL on /claim|/create|/summary.
      flushSync(() => {
        setClaimBadgePrime(null);
        setPendingClaimBadgeId(null);
        setSubmissionCode(pasteCode);
        setSaveDisabled(true);
        setShowSubmissionScreen(true);
        setMode('splash');
        setSelectedProfileID(null);
        setSubmissionCopiedFlash(false);
      });
      window.history.replaceState(window.history.state, '', '/');
      navigate('/', { replace: true });
    },
    [navigate]
  );

  const copySubmissionCode = useCallback(async () => {
    if (!submissionCode) return;
    try {
      await navigator.clipboard.writeText(submissionCode);
    } catch {
      // Fallback for restricted clipboard contexts
      const ta = document.createElement('textarea');
      ta.value = submissionCode;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setSubmissionCopiedFlash(true);
    window.setTimeout(() => setSubmissionCopiedFlash(false), 1400);
  }, [submissionCode]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (mode === 'editing_profile' && !saveDisabled && !showSubmissionScreen) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [mode, saveDisabled, showSubmissionScreen]);

  const hasUnsavedClaimEdits = mode === 'editing_profile' && !saveDisabled && !showSubmissionScreen;
  const claimNavBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!hasUnsavedClaimEdits) return false;
    // Stay inside the claim flow (claim ↔ summary ↔ create) without prompting.
    if (isClaimFlowPath(currentLocation.pathname) && isClaimFlowPath(nextLocation.pathname)) {
      return false;
    }
    return true;
  });
  const claimLeavePromptedRef = useRef(false);

  useEffect(() => {
    if (claimNavBlocker.state !== 'blocked') {
      claimLeavePromptedRef.current = false;
      return;
    }
    if (claimLeavePromptedRef.current) return;
    claimLeavePromptedRef.current = true;
    const leave = window.confirm('You have unsaved changes. Leave without saving?');
    if (leave) claimNavBlocker.proceed?.();
    else {
      claimLeavePromptedRef.current = false;
      claimNavBlocker.reset?.();
    }
  }, [claimNavBlocker]);

  useEffect(() => {
    if (profilesMap.size === 1 && isAuthenticated) {
      window.location.reload();
    }
  }, [isAuthenticated, profilesMap.size]);

  const ensureOwnProfile = useCallback(() => {
    if (hasOwnProfile && myProfileID) {
      setSelectedProfileID(myProfileID);
      return true;
    }
    return false;
  }, [hasOwnProfile, myProfileID]);

  const handleNavigate = useCallback(
    (nav: ShellNavId) => {
      const path = pathForNav(nav, hasOwnProfile ? myProfileID : null);
      if (
        nav === 'collected' ||
        nav === 'claim' ||
        nav === 'journeys' ||
        nav === 'submitted' ||
        nav === 'profile_feed'
      ) {
        if (!ensureOwnProfile()) {
          navigate('/create');
          setMode('editing_profile');
          return;
        }
      }
      if (
        nav === 'feed' ||
        nav === 'badge_gallery' ||
        nav === 'journey_gallery' ||
        nav === 'stats' ||
        nav === 'badge_hashes' ||
        nav === 'leaderboard' ||
        nav === 'explore' ||
        nav === 'badges_battle' ||
        nav === 'battle_how_to' ||
        nav === 'battle_arena' ||
        nav === 'battle_leaderboards'
      ) {
        setSelectedProfileID(null);
      }
      if (nav === 'feed' || nav === 'explore') {
        setFeedFocusId(null);
      }
      // Set mode in the same turn as clearing the profile so the sidebar never
      // flashes "My Badges" while mode is still collected/submitted/journeys.
      if (nav === 'feed') setMode('splash');
      else if (nav === 'explore') setMode('explore');
      else if (nav === 'badge_gallery') setMode('badge_gallery');
      else if (nav === 'journey_gallery') setMode('journey_gallery');
      else if (nav === 'stats') setMode('stats');
      else if (nav === 'badge_hashes') setMode('badge_hashes');
      else if (nav === 'leaderboard') setMode('leaderboard');
      else if (nav === 'badges_battle') setMode('badges_battle');
      else if (nav === 'battle_how_to') setMode('battle_how_to');
      else if (nav === 'battle_arena') setMode('battle_arena');
      else if (nav === 'battle_leaderboards') setMode('battle_leaderboards');
      else if (nav === 'claim') setMode('editing_profile');
      else if (nav === 'collected') setMode('collected');
      else if (nav === 'journeys') setMode('journeys');
      else if (nav === 'profile_feed') setMode('profile_feed');
      else if (nav === 'submitted') setMode('submitted');
      navigate(path);
    },
    [ensureOwnProfile, hasOwnProfile, myProfileID, navigate]
  );

  const handleSubmissionRefresh = useCallback(async () => {
    // Drop the submission sheet and replay the boot loader so Refresh doesn't look stuck.
    setShowSubmissionScreen(false);
    setSubmissionCode('');
    setBootLeaving(false);
    setBootOverlay(true);
    setForcedLoadingPhase('Loading Badges');
    setLoadingSnapEpoch((n) => n + 1);

    // Hold Badges for the same min as boot, then Profiles while the sheet reloads.
    await new Promise<void>((resolve) => {
      window.setTimeout(() => {
        setForcedLoadingPhase('Loading Profiles');
        resolve();
      }, LOADING_LABEL_MIN_VISIBLE_MS);
    });

    await refreshProfiles();
    setForcedLoadingPhase(null);
    handleNavigate('feed');
  }, [handleNavigate, refreshProfiles]);

  const handleClaimNewBadges = useCallback(() => {
    setPendingClaimBadgeId(null);
    setClaimBadgePrime(null);
    if (!ensureOwnProfile()) {
      setMode('editing_profile');
      navigate('/create');
      return;
    }
    setSaveDisabled(true);
    navigate(claimPath(myProfileID));
  }, [ensureOwnProfile, myProfileID, navigate]);

  const handleClaimThisBadge = useCallback(
    (badge: Badge) => {
      setSelectedBadge(null);
      if (!ensureOwnProfile()) {
        setMode('editing_profile');
        navigate('/create');
        return;
      }
      setPendingClaimBadgeId(badge.id);
      setClaimBadgePrime({ id: badge.id });
      setSaveDisabled(true);
      navigate(SUMMARY_PATH);
    },
    [ensureOwnProfile, navigate]
  );

  const handleClaimBadgePrimeConsumed = useCallback(() => {
    setClaimBadgePrime(null);
  }, []);

  const handleDevMockProfile = useCallback(() => {
    // Exit no-profile preview
    if (devEmptyProfile) {
      setDevEmptyProfile(false);
      return;
    }

    // Prefer a navigated profile page, else the profile focused in the feed.
    const targetId = selectedProfileID ?? (mode === 'splash' ? feedFocusId : null);

    if (targetId) {
      setDevEmptyProfile(false);
      setDevMockProfileID((prev) => (prev === targetId ? null : targetId));
      return;
    }

    // Feed with nobody selected (or non-profile view) → toggle no-profile experience
    setDevMockProfileID(null);
    setDevEmptyProfile((prev) => !prev);
  }, [devEmptyProfile, selectedProfileID, mode, feedFocusId]);

  const submissionsCount = useMemo(() => {
    if (!myProfile) return 0;
    return contributorBadgeCount(getContributorIds(contributorIndex, myProfile.email));
  }, [myProfile, contributorIndex]);

  const shellBadgesMap = updatedBadgesMap ?? badgesMap;
  // Single shared feed derivation — AppShell / Feed / profile feed all consume this.
  // Feed events use catalog rules only; ownership metadata must not trigger a full rebuild.
  const feedUpdates = useFeedUpdates(submissionsByEmail, effectiveProfilesMap, badgesMap);
  // Bump lastActivityAt from feed (auto-grants, etc.) once — UI reads profile.lastActivityAt.
  const { profilesMap: shellProfilesMap, rankedProfiles: shellRankedProfiles } = useMemo(
    () => withFeedActivityOnProfiles(effectiveProfilesMap, effectiveRankedProfiles, feedUpdates, badgesMap),
    [effectiveProfilesMap, effectiveRankedProfiles, feedUpdates, badgesMap]
  );
  const talosData = useMemo(
    () => ({
      profilesMap: shellProfilesMap,
      rankedProfiles: shellRankedProfiles,
      badgesMap: shellBadgesMap,
      feedUpdates,
      submissionsByEmail,
      contributorIndex,
    }),
    [shellProfilesMap, shellRankedProfiles, shellBadgesMap, feedUpdates, submissionsByEmail, contributorIndex]
  );

  if (isAuthenticating) {
    return (
      <div className="loading light">
        <p className="loading-label">Authenticating</p>
        <img src={talosBadgesFaviconURL} alt="" className="spinning" width={20} height={20} />
      </div>
    );
  }

  // Keep the scrolling field mounted while loading, but don't show the login
  // card until the badge catalog is ready.
  if (!isAuthenticated) {
    return <SlackLoginScreen ready={imagesReady && badgesMap.size > 0} loadingLabel="Loading Badges" />;
  }

  if (window.innerWidth < 350) {
    return <center className="loading light">Screen width too small.</center>;
  }

  const isCreateFlow = mode === 'editing_profile' && selectedProfileID == null;

  return (
    <div className={`App app-redesign${showSubmissionScreen ? ' app--submission' : ''}`}>
      <TalosDataProvider value={talosData}>
        <AppShell
          mode={mode}
          profile={
            isCreateFlow || devEmptyProfile
              ? null
              : myProfileID
                ? (shellProfilesMap.get(myProfileID) ?? myProfile)
                : null
          }
          onRefreshFeed={refreshProfiles}
          isRefreshingFeed={isRefreshing}
          avatarUrl={isCreateFlow || devEmptyProfile ? undefined : user?.image}
          submissionsCount={isCreateFlow || devEmptyProfile ? 0 : submissionsCount}
          selectedProfileID={
            selectedProfileID ??
            (mode === 'collected' || mode === 'submitted' || mode === 'journeys' || mode === 'profile_feed'
              ? myProfileID
              : null)
          }
          onNavigate={handleNavigate}
          onClaimNewBadges={handleClaimNewBadges}
          onSubmitBadgeIdea={() => window.open(BADGE_SUBMISSION_URL, '_blank')}
          onSignOut={logout}
          onDevMockProfile={handleDevMockProfile}
          devMockProfileID={devMockProfileID}
          devEmptyProfile={devEmptyProfile}
          feedFocusId={feedFocusId}
          onFeedFocusChange={setFeedFocusId}
          setSelectedProfileID={setSelectedProfileID}
          setMode={setMode}
          setSelectedBadge={setSelectedBadge}
          onSubmissionTriggered={handleSubmissionTriggered}
          onSaveDisabledChange={setSaveDisabled}
          navigate={navigate}
          claimBadgePrime={claimBadgePrime}
          onClaimBadgePrimeConsumed={handleClaimBadgePrimeConsumed}
          deferChrome={!dataReady || !fontsReady || forcedLoadingPhase != null}
          onBackdropFieldReady={setBackdropFieldReady}
        />
        {bootOverlay && !showSubmissionScreen ? (
          <div
            className={`app-boot-overlay app-boot-overlay--field${bootLeaving ? ' is-leaving' : ''}`}
            aria-hidden={bootLeaving}
          >
            {profilesError && !profilesReady ? (
              <>
                <p className="loading-label">Could not load profiles</p>
                <button type="button" className="app-boot-retry" onClick={() => void refreshProfiles()}>
                  Try again
                </button>
              </>
            ) : showLoadingMessage ? (
              <>
                <p className="loading-label">{loadingLabel}</p>
                <img src={talosBadgesFaviconURL} alt="" className="spinning" width={20} height={20} />
              </>
            ) : null}
          </div>
        ) : null}
        {selectedBadge && updatedBadgesMap && !showSubmissionScreen ? (
          <Modal
            badge={updatedBadgesMap.get(selectedBadge.id) ?? selectedBadge}
            onClose={(openSubmitted: boolean) => {
              setSelectedBadge(null);
              if (openSubmitted) {
                handleNavigate('submitted');
              }
            }}
            setSelectedProfileID={(id) => handleSetProfileID(id)}
            mode={mode}
            setMode={setMode}
            selectedProfileID={selectedProfileID}
            onClaimThisBadge={handleClaimThisBadge}
            onSelectBadge={setSelectedBadge}
            currentUserBadgeIDs={myProfile?.badgesIDs}
            currentUserProfileId={myProfileID}
          />
        ) : null}
      </TalosDataProvider>
      {showSubmissionScreen ? (
        <SubmissionOverlay
          submissionCode={submissionCode}
          submissionCopiedFlash={submissionCopiedFlash}
          onCopyCode={copySubmissionCode}
          onRefresh={() => void handleSubmissionRefresh()}
          refreshDisabled={isRefreshing || forcedLoadingPhase != null}
        />
      ) : null}
    </div>
  );
};
